import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Mail } from "lucide-react";
import { STORE, api } from "../lib/api";
import { toast } from "sonner";

const NewsletterForm = () => {
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) {
            toast.error("Please tick the consent box to subscribe");
            return;
        }
        setBusy(true);
        try {
            await api.post("/newsletter/subscribe", { email, consent });
            setDone(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not subscribe right now");
        } finally {
            setBusy(false);
        }
    };

    if (done)
        return (
            <p className="text-[#40E0D0] text-sm font-bold" data-testid="newsletter-success">
                You're subscribed — thank you!
            </p>
        );
    return (
        <form onSubmit={submit} className="space-y-3" data-testid="newsletter-form">
            <div className="flex gap-2">
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    data-testid="newsletter-email"
                    className="flex-1 min-w-0 rounded-full px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#40E0D0]"
                />
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="newsletter-submit"
                    className="rounded-full bg-[#40E0D0] px-5 py-2.5 text-sm font-bold text-[#0033A0] hover:bg-[#2fd4c4] transition-colors disabled:opacity-60"
                >
                    Join
                </button>
            </div>
            <label className="flex items-start gap-2 text-xs text-blue-200 cursor-pointer">
                <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="newsletter-consent"
                    className="mt-0.5 accent-[#40E0D0]"
                />
                I'm happy to receive occasional news and offers by email. Unsubscribe anytime.
            </label>
        </form>
    );
};

export const Footer = () => {
    return (
        <footer data-testid="site-footer" className="bg-[#0033A0] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                    <div>
                        <img
                            src="/assets/logo.webp"
                            alt="Freddie's Afro Caribbean Store logo"
                            className="h-12 w-auto rounded-md mb-4"
                            data-testid="footer-logo"
                        />
                        <p className="text-blue-100 text-sm leading-relaxed" data-testid="footer-tagline">
                            Your local destination for African and Caribbean groceries, meat, poultry and fresh produce.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-heading font-bold text-lg mb-4 text-[#40E0D0]">Visit Us</h3>
                        <address className="not-italic space-y-3 text-sm text-blue-100">
                            <p className="flex items-start gap-2" data-testid="footer-address">
                                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#40E0D0]" />
                                {STORE.address}
                            </p>
                            {STORE.phones.map((p) => (
                                <p key={p.tel} className="flex items-center gap-2">
                                    <Phone className="h-4 w-4 shrink-0 text-[#40E0D0]" />
                                    <a
                                        href={`tel:${p.tel}`}
                                        data-testid={`footer-phone-${p.tel.slice(-4)}`}
                                        className="hover:text-[#40E0D0] transition-colors underline-offset-4 hover:underline"
                                    >
                                        {p.label}
                                    </a>
                                </p>
                            ))}
                            <p className="flex items-center gap-2">
                                <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-[#40E0D0]" aria-hidden="true">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                                <a
                                    href={STORE.whatsapp.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-whatsapp"
                                    className="hover:text-[#40E0D0] transition-colors underline-offset-4 hover:underline"
                                >
                                    WhatsApp {STORE.whatsapp.label}
                                </a>
                            </p>
                            <p className="flex items-center gap-2">
                                <Mail className="h-4 w-4 shrink-0 text-[#40E0D0]" />
                                <a
                                    href={`mailto:${STORE.email}`}
                                    data-testid="footer-email"
                                    className="hover:text-[#40E0D0] transition-colors underline-offset-4 hover:underline break-all"
                                >
                                    {STORE.email}
                                </a>
                            </p>
                        </address>
                    </div>

                    <div>
                        <h3 className="font-heading font-bold text-lg mb-4 text-[#40E0D0]">Opening Hours</h3>
                        <ul className="space-y-2 text-sm text-blue-100" data-testid="footer-hours">
                            {STORE.hours.map((h) => (
                                <li key={h.days} className="flex items-start gap-2">
                                    <Clock className="h-4 w-4 mt-0.5 shrink-0 text-[#40E0D0]" />
                                    <span>
                                        <span className="font-bold text-white">{h.days}</span>
                                        <br />
                                        {h.time}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-heading font-bold text-lg mb-4 text-[#40E0D0]">
                            <Mail className="inline h-4 w-4 mr-1 -mt-0.5" /> Stay in Touch
                        </h3>
                        <NewsletterForm />
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-blue-200">
                    <p data-testid="footer-copyright">© {new Date().getFullYear()} {STORE.name}. All rights reserved.</p>
                    <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label="Footer navigation">
                        <Link to="/" className="hover:text-[#40E0D0] transition-colors" data-testid="footer-nav-home">Home</Link>
                        <Link to="/products" className="hover:text-[#40E0D0] transition-colors" data-testid="footer-nav-products">Our Products</Link>
                        <Link to="/about" className="hover:text-[#40E0D0] transition-colors" data-testid="footer-nav-about">About Us</Link>
                        <Link to="/contact" className="hover:text-[#40E0D0] transition-colors" data-testid="footer-nav-contact">Contact Us</Link>
                        <Link to="/privacy" className="hover:text-[#40E0D0] transition-colors" data-testid="footer-nav-privacy">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-[#40E0D0] transition-colors" data-testid="footer-nav-terms">Terms of Use</Link>
                        <Link to="/admin" className="hover:text-[#40E0D0] transition-colors" data-testid="footer-nav-admin">Staff Login</Link>
                    </nav>
                    <a
                        href="https://operamind.uk/web"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-powered-by"
                        aria-label="Powered by OperaMind Web — visit operamind.uk/web"
                        className="inline-flex items-center hover:-translate-y-0.5 transition-transform"
                    >
                        <img
                            src="/assets/operamind-web-transparent.png"
                            alt="Powered by OperaMind Web"
                            className="h-10 w-auto [filter:drop-shadow(0_0_6px_rgba(255,255,255,0.45))_drop-shadow(0_0_1px_rgba(255,255,255,0.9))]"
                        />
                    </a>
                </div>
            </div>
        </footer>
    );
};
